import React, {useRef, useState, useEffect, useCallback} from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Navbar, ContactCTA, Footer, LazyImage } from '../../components';
import { getProjectById, getRelated } from '../../data/projects';
import styles from './ProjectDetail.module.css';

const fadeUp = (delay = 0) => ({
  hidden:  { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut', delay } },
});

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ images, index, onClose }) {
  const [current, setCurrent] = useState(index);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const prev = useCallback(() => setCurrent(i => (i - 1 + images.length) % images.length));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const next = useCallback(() => setCurrent(i => (i + 1) % images.length));

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev, onClose]);

  // Lock scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <motion.div
      className={styles.lightboxBackdrop}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      {/* Close */}
      <button className={styles.lightboxClose} onClick={onClose} aria-label="Close">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path d="M3 3L15 15M3 15L15 3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>

      {/* Counter */}
      <span className={styles.lightboxCounter}>{current + 1} / {images.length}</span>

      {/* Prev */}
      <button
        className={`${styles.lightboxNav} ${styles.lightboxNavPrev}`}
        onClick={e => { e.stopPropagation(); prev(); }}
        aria-label="Previous image"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Image */}
      <AnimatePresence mode="wait">
        <motion.img
          key={current}
          src={images[current]}
          alt={`Image ${current + 1}`}
          className={styles.lightboxImg}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1, transition: { duration: 0.22 } }}
          exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.15 } }}
          onClick={e => e.stopPropagation()}
          draggable={false}
        />
      </AnimatePresence>

      {/* Next */}
      <button
        className={`${styles.lightboxNav} ${styles.lightboxNavNext}`}
        onClick={e => { e.stopPropagation(); next(); }}
        aria-label="Next image"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M9 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </motion.div>
  );
}

// ── Image Gallery ─────────────────────────────────────────────────────────────
function Gallery({ images, video }) {
  const trackRef = useRef(null);
  const [tab, setTab] = useState('images');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const scroll = (dir) => {
    if (trackRef.current) {
      const item = trackRef.current.firstChild;
      const w = item ? item.offsetWidth + 24 : trackRef.current.offsetWidth;
      trackRef.current.scrollBy({ left: dir * w, behavior: 'smooth' });
    }
  };

  return (
    <>
      <div className={styles.gallery}>
        <div className={styles.galleryHeader}>
          <div className={styles.galleryTabs}>
            <button
              className={tab === 'images' ? styles.galleryLabel : styles.galleryVideoBtn}
              onClick={() => setTab('images')}
            >Image Gallery</button>
            <button
              className={tab === 'video' ? styles.galleryLabel : styles.galleryVideoBtn}
              onClick={() => setTab('video')}
            >Video</button>
          </div>
          {tab === 'images' && (
            <div className={styles.galleryNav}>
              <button className={styles.navBtnLeft} onClick={() => scroll(-1)} aria-label="Previous images">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18l-6-6 6-6" stroke="#5e5e5e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className={styles.navBtnRight} onClick={() => scroll(1)} aria-label="Next images">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 6l6 6-6 6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}
        </div>

        {tab === 'images' ? (
          <div className={styles.galleryTrack} ref={trackRef}>
            {images.map((src, i) => (
              <button
                key={i}
                className={styles.galleryItem}
                onClick={() => setLightboxIndex(i)}
                aria-label={`View image ${i + 1} fullscreen`}
              >
                <LazyImage src={src} alt={`Gallery image ${i + 1}`} className={styles.galleryImg} />
                <span className={styles.galleryExpandHint} aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M3 9V3h6M15 3h6v6M3 15v6h6M15 21h6v-6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </button>
            ))}
          </div>
        ) : video ? (
          <video
            className={styles.videoPlayer}
            src={video}
            controls
            playsInline
          />
        ) : (
          <div className={styles.galleryEmpty}>
            <p className={styles.galleryEmptyText}>No videos available</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox
            images={images}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// ── Project Meta Accordion ─────────────────────────────────────────────────────
function ProjectMeta({ project }) {
  const [open, setOpen] = useState(true);

  const rows = [
    { label: 'Service',  value: project.type     },
    { label: 'Location', value: project.location },
    { label: 'Year',     value: project.year     },
    { label: 'Client',   value: project.client   },
    { label: 'Status',   value: project.status   },
  ];

  return (
    <div className={styles.metaSection}>
      <button
        className={styles.metaHeader}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={styles.metaHeaderTitle}>Project Meta</span>
        <span className={`${styles.metaChevronWrap} ${open ? styles.open : ''}`}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 9l7 7 7-7" stroke="#5e5e5e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className={styles.metaBody}
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className={styles.metaBodyInner}>
              {rows.map(({ label, value }) => (
                <div key={label} className={styles.metaRow}>
                  <span className={styles.metaRowLabel}>{label}</span>
                  <span className={styles.metaRowValue}>{value || '—'}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── More Project Card ─────────────────────────────────────────────────────────
function MoreCard({ project }) {
  return (
    <Link to={`/projects/${project.id}`} className={styles.moreCard}>
      <div className={styles.moreCardImg}>
        <LazyImage src={project.image} alt={project.title} className={styles.moreCardImgEl} />
        <div className={styles.moreCardOverlay} aria-hidden="true" />
      </div>
      <div className={styles.moreCardBody}>
        <div className={styles.moreCardText}>
          <p className={styles.moreCardTitle}>{project.title}</p>
          <p className={styles.moreCardMeta}>{project.year}&nbsp;&nbsp;{project.location}</p>
        </div>
        <span className={styles.moreCardArrow} aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M4.5 10h11M10 4.5l5.5 5.5-5.5 5.5" stroke="#404040" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>
    </Link>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function ProjectDetail() {
  const { id } = useParams();
  const moreTrackRef = useRef(null);
  const project = getProjectById(id);

  if (!project) return <Navigate to="/projects" replace />;

  const related = getRelated(project);

  const scrollMore = (dir) => {
    if (moreTrackRef.current) {
      const card = moreTrackRef.current.firstChild;
      const w = card ? card.offsetWidth + 24 : moreTrackRef.current.offsetWidth / 2;
      moreTrackRef.current.scrollBy({ left: dir * w, behavior: 'smooth' });
    }
  };

  return (
    <div className={styles.page}>
      <Navbar theme="light" cta="Contact Us" ctaTo="/contact" />

      {/* ── BREADCRUMB ─────────────────────────────────────── */}
      <div className={styles.breadcrumb}>
        <Link to="/projects" className={styles.breadLink}>Projects</Link>
        <span className={styles.breadCurrent}>/ {project.title.toUpperCase()}</span>
      </div>

      {/* ── HERO IMAGE ─────────────────────────────────────── */}
      <div className={styles.heroWrap}>
        <LazyImage src={project.image} alt={project.title} className={styles.heroImg} />
        <div className={styles.heroOverlay} aria-hidden="true" />
        <div className={styles.heroBottom}>
          <p className={styles.heroTitle}>{project.title.toUpperCase()}</p>
          <div className={styles.heroTags}>
            <span className={styles.heroTag}>{project.category}</span>
          </div>
        </div>
      </div>

      {/* ── CONTENT ────────────────────────────────────────── */}
      <motion.section
        className={styles.content}
        variants={fadeUp(0)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className={styles.contentLeft}>
          <p className={styles.contentHeading}>Project Type</p>
          <p className={styles.projectTypeRed}>{project.type}</p>
        </div>
        <div className={styles.contentRight}>
          <p className={styles.contentHeading}>Description</p>
          <p className={styles.description}>{project.description}</p>
        </div>
      </motion.section>

      {/* ── IMAGE GALLERY ──────────────────────────────────── */}
      <motion.div
        variants={fadeUp(0)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <Gallery images={project.gallery} video={project.video} />
      </motion.div>

      {/* ── PROJECT META ───────────────────────────────────── */}
      <motion.div
        variants={fadeUp(0)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <ProjectMeta project={project} />
      </motion.div>

      {/* ── SPACER ─────────────────────────────────────────── */}
      <div className={styles.metaSpacer} aria-hidden="true" />

      {/* ── MORE PROJECTS ──────────────────────────────────── */}
      {related.length > 0 && (
        <motion.section
          className={styles.moreSection}
          variants={fadeUp(0)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          <div className={styles.moreHeader}>
            <h2 className={styles.moreTitle}>More Project</h2>
            <div className={styles.moreNav}>
              <button className={styles.moreNavBtn} onClick={() => scrollMore(-1)} aria-label="Previous projects">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18l-6-6 6-6" stroke="#5e5e5e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className={styles.moreNavBtn} onClick={() => scrollMore(1)} aria-label="Next projects">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 6l6 6-6 6" stroke="#5e5e5e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          <div className={styles.moreTrack} ref={moreTrackRef}>
            {related.map((p) => (
              <MoreCard key={p.id} project={p} />
            ))}
          </div>
        </motion.section>
      )}

      <ContactCTA />
      <Footer />
    </div>
  );
}
